import React, { useEffect, useMemo, useState } from "react";
import "./NotificationSetting.css";
import MainLayout from "components/layout/main-layout/MainLayout";
import { titles } from "constants/titles";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  getNotificationSettings,
  updateNotificationSettings,
  getNotificationPermissionLogs,
} from "redux/Notifications/actions";
import { convertInRequiredTimeZone, isSessionTimeExpired } from "utility";
import { setSessionExpiryModalVisibility } from "redux/auth/actions";
import CustomSelectBox from "components/widgets/custom-select-box/CustomSelectBox";
import { ToggleOn, ToggleOff } from "assets/SVGs/SVGs";

/**
 * Permission logs `user_type` query allows only enum: `customer` | `guest_user`
 * (must match list API `user_type` after trim, lower-case, spaces → underscores).
 */
const mapListUserTypeToPermissionLogsParam = (listUserType) => {
  if (listUserType == null || listUserType === "") {
    return null;
  }
  const normalized = String(listUserType)
    ?.trim()
    ?.toLowerCase()
    ?.replace(/\s+/g, "_");
  if (normalized === "customer") {
    return "customer";
  }
  if (normalized === "guest_user") {
    return "guest_user";
  }
  return null;
};

const NotificationSetting = () => {
  const [snackBarMessage, setSnackBarMessage] = useState({
    message: "",
    type: "",
  });
  const [notificationSettings, setNotificationSettings] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  /** Row index into `notificationSettings` / list API order — required when the same `device_id` appears for customer and guest_user. */
  const [selectedRowIndex, setSelectedRowIndex] = useState("");
  const [activeTab, setActiveTab] = useState("settings"); // settings | logs
  const [permissionLogs, setPermissionLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const isLoading = useSelector((store) => store?.notifications?.loading);
  const settingsList = useSelector(
    (store) => store?.notifications?.notificationSettings
  );
  const { currentTimeZone } = useSelector((store) => store?.common);
  const userData = useSelector((store) => store?.auth?.userData);
  const dispatch = useDispatch();
  const history = useHistory();

  const selectedDeviceDetails = useMemo(() => {
    if (selectedRowIndex === "" || selectedRowIndex == null) {
      return null;
    }
    const idx = Number(selectedRowIndex);
    if (Number.isNaN(idx) || idx < 0) {
      return null;
    }
    return notificationSettings?.[idx] ?? null;
  }, [notificationSettings, selectedRowIndex]);

  /**
   * Permission logs `user_type` query param comes only from the notification settings list row
   * for the selected device (`customer` or `guest_user`), never from the logged-in session.
   */
  const logsQueryUserType = useMemo(() => {
    const listUserType =
      selectedDeviceDetails?.user_type ?? selectedDeviceDetails?.userType;
    return mapListUserTypeToPermissionLogsParam(listUserType);
  }, [selectedDeviceDetails?.user_type, selectedDeviceDetails?.userType]);

  useEffect(() => {
    setTimeout(() => {
      let params = {
        userType: userData?.isGuestUser ? "guestuser" : "accountowner",
        history,
        dispatch,
      };
      getNotificationSettings(params);
    }, 600);
  }, []);

  useEffect(() => {
    if (settingsList) {
      const list = settingsList || [];
      const filtedList = list?.filter(
        (item) => item?.notifications_settings !== null
      );
      setNotificationSettings(filtedList);
      setDeviceList(
        filtedList?.map((item, index) => {
          const deviceTypeLabel = item?.device_type
            ? String(item?.device_type)?.toLowerCase()
            : "";
          const userTypeLabel = item?.user_type
            ? String(item?.user_type)?.replace(/_/g, " ")
            : "";
          const labelParts = [item?.device_id, deviceTypeLabel, userTypeLabel]?.filter(
            (part) => part !== "" && part != null
          );
          return {
            label: labelParts?.join(" - "),
            id: index + 1,
            value: String(index),
          };
        })
      );
    }
  }, [settingsList]);

  useEffect(() => {
    const deviceId = selectedDeviceDetails?.device_id;
    if (activeTab !== "logs" || !deviceId) {
      return;
    }
    if (!logsQueryUserType) {
      setPermissionLogs([]);
      setLogsLoading(false);
      return;
    }

    let isCancelled = false;
    (async () => {
      setLogsLoading(true);
      const logs = await getNotificationPermissionLogs({
        userType: userData?.isGuestUser ? "guestuser" : "accountowner",
        history,
        deviceId,
        queryUserType: logsQueryUserType,
      });
      if (!isCancelled) {
        setPermissionLogs(Array.isArray(logs) ? logs : []);
        setLogsLoading(false);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [
    activeTab,
    selectedDeviceDetails?.device_id,
    selectedDeviceDetails?.user_type,
    selectedDeviceDetails?.userType,
    selectedRowIndex,
    userData?.isGuestUser,
    logsQueryUserType,
  ]);

  const handleChange = async (item) => {
    let timeDifference = await isSessionTimeExpired();
    if (timeDifference && !userData?.isGuestUser) {
      setSessionExpiryModalVisibility(dispatch, true);
      return;
    } else {
      let params = {
        userType: userData?.isGuestUser ? "guestuser" : "accountowner",
        setSnackBarMessage,
        history,
        dispatch,
        item,
      };
      updateNotificationSettings(params);
    }
  };

  const handleDropdownSelect = (e) => {
    setSelectedRowIndex(e?.value != null ? String(e?.value) : "");
    setPermissionLogs([]);
  };

  return (
    <MainLayout
      text={titles?.notifications}
      isBackHide={false}
      isSemiHeader={true}
      white={false}
      isLoading={isLoading}
      snackBarMessage={snackBarMessage}
    >
      {deviceList && deviceList?.length > 0 ? (
        <>
          <p className="feedback_desc_text text-white-color body_h1 mb10">
            {titles?.select_device}
          </p>
          <CustomSelectBox
            selectedValue={selectedRowIndex}
            onChange={handleDropdownSelect}
            options={deviceList}
            title={titles?.select_device}
            className={"cs_select_dropdown"}
          />
          {selectedDeviceDetails?.device_id ? (
            <>
              <div className="ns_tabs">
                <button
                  type="button"
                  className={`ns_tab_btn ${activeTab === "settings" ? "active" : ""}`}
                  onClick={() => setActiveTab("settings")}
                >
                  {titles?.settings}
                </button>
                <button
                  type="button"
                  className={`ns_tab_btn ${activeTab === "logs" ? "active" : ""}`}
                  onClick={() => setActiveTab("logs")}
                >
                  {titles?.notification_permission_logs_tab}
                </button>
              </div>

              {activeTab === "settings" && (
                <>
                  {selectedDeviceDetails?.notifications_settings?.map((alert, index) => {
                    return (
                      <div key={`alert ${index}`}>
                        <div
                          className="settings_item_container"
                          onClick={() => handleChange(alert)}
                        >
                          <div className="settings_item_content">
                            <p className="settings_item_text">
                              {alert?.notification_type}
                            </p>
                            <div className="ns_toggle_container">
                              {alert?.is_enabled ? (
                                <ToggleOn className="ns_toggle_svg" />
                              ) : (
                                <ToggleOff className="ns_toggle_svg" />
                              )}
                            </div>
                          </div>
                        </div>
                        {index === 0 && <div className="ns_first_separator"></div>}
                        {index > 0 &&
                          index < (selectedDeviceDetails?.notifications_settings?.length || 0) - 1 && (
                            <div className="settings_separator"></div>
                          )}
                      </div>
                    );
                  })}
                </>
              )}

              {activeTab === "logs" && (
                <div className="ns_logs_container">
                  <div className="ns_logs_header">
                    <div className="ns_logs_col">{titles?.notification_logs_permission}</div>
                    <div className="ns_logs_col">{titles?.notification_logs_created}</div>
                  </div>

                  {logsLoading ? (
                    <div className="ns_logs_empty">{titles?.permission_logs_loading}</div>
                  ) : permissionLogs?.length > 0 ? (
                    permissionLogs?.map((log) => {
                      const isGranted = Number(log?.permission_granted) === 1;
                      return (
                        <div className="ns_logs_row" key={`perm_log_${log?.log_id}`}>
                          <div className="ns_logs_col">
                            <span className={`ns_badge ${isGranted ? "granted" : "denied"}`}>
                              {isGranted ? titles?.granted : titles?.denied}
                            </span>
                          </div>
                          <div className="ns_logs_col ns_logs_date">
                            {log?.createdAt
                              ? convertInRequiredTimeZone(
                                  log?.createdAt,
                                  null,
                                  currentTimeZone
                                )
                              : titles?.placeholder_dash}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="ns_logs_empty">{titles?.no_permission_logs}</div>
                  )}
                </div>
              )}
            </>
          ) : null}
        </>
      ) : (
        <p className="feedback_desc_text text-white-color body_h1 mb10">
          {titles?.no_phone_active}
        </p>
      )}
    </MainLayout>
  );
};

export default NotificationSetting;
